import { Container } from '@pixi/display';
import { Graphics, LINE_CAP, LINE_JOIN } from '@pixi/graphics';
import { Pixel } from '../../../../image-module/image-processing/pixel';
import { RawImageShape } from '../../../../image-module/image-processing/raw-image-shape';
import { PathSection } from '../path-shape/path-sections/path-section';
import { Coords } from '../../../../elements/resource/types/shape.type';
import { PointController } from '../../../../elements/util/point-controller/point-controller';
import { RectangleController } from '../../../../elements/util/rectangle-controller/rectangle-controller';
import { GeneralShape } from '../general/general-shape';
import { Subscription } from 'rxjs';
import { TextShape } from '../text/text-shape';
import { Mesh, Shader, Sprite, Text } from 'pixi.js';
import { GradientDirection, GradientStop } from './rectangle-element';
import { StoryBoardItem } from '../../../../services/canvas/story-board/story-board-item';
import { VideoShape } from '../base/video-shape';
import { StudioTextShape } from '../../../store/studio/text/studio-text-shape';
import { StudioShape } from '../../../store/studio/shapes/studio-shape';
import { StudioSceneShape } from '../general/root/studio-scene-shape';
import { GradientBackground } from '../background/gradient-background';

export const convertHexToNumber = (value: string | number): number => {
  if (!value) {
    return 0;
  }

  if (!isNaN(value as number)) {
    return value as number;
  }

  if (value == 'white') {
    return 16777215;
  }

  if (value == 'black') {
    return 0;
  }

  if (typeof value == 'object') {
    return 0;
  }
  return Number(`0x${(value as string).substring(1)}`);
};
export interface SVGBaseConfig {
  position?: { x: number; y: number };
  x?: number;
  y?: number;
  fill?: string;
  opacity?: number;
  'fill-opacity'?: number;
  stroke?: string;
  'stroke-width'?: number;
  'stroke-opacity'?: number;
  closed?: boolean;
  noFill?: boolean;
  gradient?: boolean;
  gradientDirection?: GradientDirection;
  gradientColor?: string;

  gradientStops?: GradientStop[];
  hole?: boolean;
  noDrag?: boolean;
  noCursorPointer?: boolean;
  log?: false;
}

export type SVGConfig<T = {}> = T & Partial<SVGBaseConfig>;

// TODO - rename to CanvasElement/BaseElement
export class SVG<
  T = SVGConfig,
  ElementType extends Graphics | Text | Sprite = Graphics,
> {
  _element: ElementType;
  quad: Mesh<Shader>;

  offset: Coords;

  get element() {
    return this._element as ElementType;
  }

  get type() {
    return 'svg';
  }

  protected dragConfig?: {
    start?: () => void;
    drag?: (dx: number, dy: number) => void;
    end?: (dx?: number, dy?: number) => void;
    mouseOver?: () => void;
    mouseOut?: () => void;
  };

  get offsetX() {
    return this.offset?.[0] || 0;
  }

  get offsetY() {
    return this.offset?.[1] || 0;
  }

  get x() {
    return this.config.x;
  }

  get y() {
    return this.config.y;
  }

  get px() {
    return this.config.position?.x || 0;
  }

  get py() {
    return this.config.position?.y || 0;
  }

  get fill() {
    return this.config.fill;
  }

  get fillOpacity() {
    return this.config['fill-opacity'];
  }

  get opacity() {
    return this.config.opacity;
  }

  get stroke() {
    return this.config.stroke;
  }

  get strokeWidth() {
    return this.defaultTo(this.config['stroke-width'], 1);
  }

  get strokeOpacity() {
    return this.defaultTo(this.config['stroke-opacity'], 1);
  }

  defaultTo(val: number, def: number) {
    return val !== undefined ? val : def;
  }

  get cs() {
    return (this.parent as any).cs;
  }

  get position() {
    return this.config.position || { x: 0, y: 0 };
  }

  id: string;

  constructor(
    public parent:
      | PathSection
      | TextShape
      | GeneralShape
      | PointController
      | RawImageShape
      | Pixel
      | RectangleController
      | GradientBackground
      | VideoShape
      | StoryBoardItem
      | StudioTextShape
      | StudioSceneShape
      | StudioShape,
    protected container: Container,
    protected config: SVGConfig<T>,
    index?: number,
  ) {
    this.id = Math.random().toString();
    this.initElement();
    this.updateAttr();

    if (index !== undefined) {
      container.addChildAt(this.element, index);
    } else {
      container.addChild(this.element);
    }
  }

  initElement() {}
  mouseMoveSubscription: Subscription;

  dragBase: Coords;
  clickHandler: (coords?: Coords) => void;
  doubleClickHandler: (coords: Coords) => void;

  drag(
    drag: (dx: number, dy: number) => void,
    start?: () => void,
    end?: (dx?: number, dy?: number) => void,
  ) {
    this.dragConfig = { start, drag, end };
    this.element.eventMode = 'dynamic';
    // TODO - check what that is

    this.dragInitialized = true;
    this.initPointerdown();

    return this;
  }

  pointerDownInitialized = false;

  initPointerdown() {
    if (this.pointerDownInitialized) {
      return;
    }

    this.pointerDownInitialized = true;
    // this.element.interactive = true;
    this.element.eventMode = 'dynamic';
    this.element.cursor = 'pointer';
    this.element.on('pointerdown', e => {
      if (this.cs.draggedSVGElement) {
        return;
      }

      if (this.cs.isAlPressed) {
        return;
      }

      if (this.config.noDrag) {
        return;
      }
      this.cs.draggedSVGElement = this as SVG;

      this.startDragHandler(e.data.global.x, e.data.global.y);
    });
  }

  rightClick(handler: (e?) => void) {
    this.element.eventMode = 'dynamic';
    this.element.cursor = 'pointer';
    this.element.on('rightdown', e => {
      e.preventDefault();
      handler(e);
    });
    return this;
  }

  click(handler: (coords?: Coords) => void) {
    this.element.eventMode = 'dynamic';
    this.element.cursor = 'pointer';
    // this.element.interactive = true;
    this.clickHandler = handler;
    this.initPointerdown();

    // this.element.on('pointerup', () => {
    //   this.cs.consumeClick();
    //   this.clickHandler(handler)
    // });

    return this;
  }

  doubleclick(handler: (coords?: Coords) => void) {
    this.element.eventMode = 'dynamic';
    this.element.cursor = 'pointer';

    this.doubleClickHandler = handler;
    this.initPointerdown();

    // this.element.on('pointerup', () => {
    //   this.cs.consumeClick();
    //   this.clickHandler(handler)
    // });

    return this;
  }

  setMask(element: Graphics | Container) {
    this.element.mask = element;
  }

  mouseover(handler: (e?: SVG) => void) {
    /* 'static' – use this for simple shapes or elements that don’t move much.
    'dynamic' – use this if the object moves or its hit area changes.
    'passive' – listens to events but doesn't block interactions below it.
    'auto' – lets Pixi decide the best mode (good default).
    'none' – disables interactivity. */

    if (this.type == 'circle') {
      this.element.eventMode = 'passive';
    } else {
      this.element.eventMode = 'static';
    }

    if (!this.config.noCursorPointer) {
      this.element.cursor = 'pointer';
    }

    this.element.on('pointerenter', () => {
      // console.log('element > mouseover', this.parent);
      // console.log('element > handler', handler);

      if (this.cs.mouseOverclear) {
        return;
      }

      handler(this as SVG);
    });
    return this;
  }

  mouseout(handler: (e?: SVG) => void) {
    // this.element.eventMode = 'dynamic';
    // this.element.cursor = 'pointer';
    this.element.on('pointerleave', () => handler(this as SVG));
    return this;
  }

  dragInitialized = false;
  _unMoved: Coords;

  noDragTookPlace = false;

  startDragHandler(x: number, y: number) {
    this.noDragTookPlace = true;
    this.mouseMoveSubscription = this.cs.mouseMove.subscribe(({ x, y }) =>
      this.dragHandler(x, y),
    );
    this.dragBase = [x, y];
    this._x = x;
    this._y = y;
    this.dragConfig?.start();
  }

  _x: number;
  _y: number;

  get dxDy() {
    return [this._x - this.dragBase[0], this._y - this.dragBase[1]];
  }

  stopDrag() {
    this.mouseMoveSubscription.unsubscribe();
    this.dragBase = null;
  }

  dragHandler(x: number, y: number) {
    this.noDragTookPlace = false;
    this._y = y;
    this._x = x;
    const [x0, y0] = this.dragBase;
    this.dragConfig?.drag(
      (x - x0) / this.cs.canvasScale,
      (y - y0) / this.cs.canvasScale,
    );
  }

  firstClickArrived = false;

  secondClickArrived = false;

  endDragHandler([x, y]: Coords) {
    this.mouseMoveSubscription?.unsubscribe();
    if (this.noDragTookPlace || !this.dragConfig) {
      this.dragConfig?.end?.(0, 0);

      if (this.firstClickArrived) {
        this.doubleClickHandler([x, y]);
        this.firstClickArrived = false;
        this.secondClickArrived = true;
        return;
      }

      // -- // -- //
      this.clickHandler?.([x, y]);
      if (this.doubleClickHandler) {
        this.firstClickArrived = true;
        setTimeout(() => {
          if (this.secondClickArrived) {
            this.secondClickArrived = false;
            return;
          }

          // the second click did not arrive
          this.firstClickArrived = false;
        }, 200);
      }

      // -- //
      return;
    }

    if (!this.dragBase) {
      console.warn('svgElement > dragBase could not be found!');
      return;
    }
    const [x0, y0] = this.dragBase;
    this.dragConfig.end?.(
      (this._x - x0) / this.cs.canvasScale,
      (this._y - y0) / this.cs.canvasScale,
    );
  }

  patch(config: Partial<SVGConfig<T>>, show = false, log = false) {
    if (show) {
      this.show();
    }

    // if (this.element.destroyed) {
    //   return;
    // }

    this.config = {
      ...this.config,
      ...config,
    };
    if (!this.element) {
      console.warn('No element has been found for SVG');
      return;
    }

    this.updateAttr();
  }

  setEventMode(mode: 'static' | 'dynamic' | 'passive' | 'auto' | 'none') {
    this.element.eventMode = mode;
  }

  clear() {
    try {
      (this.element as Graphics)?.clear();
    } catch (e) {
      // console.warn(`Unable to clear element`);
      return;
    }
  }

  hidden = false;

  show(strokeWidth?: number) {
    this.hidden = false;
    this.element.visible = true;
    if (strokeWidth !== undefined) {
      this.config['stroke-width'] = strokeWidth;
      this.updateAttr();
    }
  }

  hide() {
    // console.log('svg.hide', this);
    this.hidden = true;
    this.element.visible = false;
  }

  get visible() {
    return this.element.visible;
  }

  remove() {
    if (!this.element) {
      return;
    }

    if (!this.element.destroyed) {
      this.element.destroy();
    }
  }

  disableActions() {
    this.element.eventMode = 'none'; // disables all interaction
    this.element.cursor = null;
  }

  enableActions() {
    this.element.eventMode = 'dynamic'; // or 'dynamic', if the object moves
    this.element.cursor = 'pointer';
  }

  get isStrokeDefined() {
    // return this.stroke !== undefined || !!this.strokeWidth;
    return this.stroke !== undefined && this.stroke !== null;
  }

  get isFillDefined() {
    return this.fill !== undefined && this.fill !== null;
  }

  updateAttr() {
    // if (this instanceof PathElement) {
    //   console.log('isFillDefined', this.isFillDefined, this);
    // }
    if (!this.element) {
      return;
    }

    if (this.element instanceof Text) {
      return;
    }

    this.clear();

    if (this.isFillDefined) {
      // --> // --> // --> //
      const fill = convertHexToNumber(this.fill);
      try {
        (this.element as Graphics)?.beginFill(
          fill < 16777216 ? fill : 0,
          this.opacity === undefined && this.fillOpacity === undefined
            ? 1
            : this.opacity || this.fillOpacity,
        );
      } catch (error) {
        console.log('beginFill-error', this.element, fill);
        return;
      }
    } else if (!this.config.noFill) {
      // This is create for the click and hover events

      if (this.element.destroyed) {
        console.warn('element is destroyed');
        return;
      }
      // so that we can select it
      (this.element as Graphics).beginFill('#000', 0.001);
    }

    if (this.isStrokeDefined) {
      try {
        (this.element as Graphics)?.lineStyle({
          width: isNaN(this.strokeWidth) ? 1 : this.strokeWidth,
          color: convertHexToNumber(this.stroke) || 0x000000,
          alpha: this.strokeOpacity || 1,
          cap: LINE_CAP.ROUND,
          join: LINE_JOIN.ROUND,
        });
      } catch (e) {
        console.log(
          'error',
          {
            width: isNaN(this.strokeWidth) ? 1 : this.strokeWidth,
            color: convertHexToNumber(this.stroke) || 0x000000,
            alpha: this.strokeOpacity || 1,
            cap: LINE_CAP.ROUND,
            join: LINE_JOIN.ROUND,
          },
          e,
        );
      }
    }

    /*
    const ctx = this.cs.canvas.getContext('2d');

    const quality = 256;

    // use canvas2d API to create gradient
    const grd = ctx.createLinearGradient(0, 0, quality, 0);

    grd.addColorStop(0, 'rgba(255, 255, 255, 0.0)');
    grd.addColorStop(0.3, 'cyan');
    grd.addColorStop(0.7, 'red');
    grd.addColorStop(1, 'green');
    */
    this.updatePosition();
  }

  updatePosition() {
    if (this.config.log) {
      console.log('updatePosition', this.config.position);
    }
    if (this.config.position || this.offsetX || this.offsetY) {
      const { x, y } = this.config.position || { x: 0, y: 0 };
      (this.element as Graphics).moveTo(x + this.offsetX, y + this.offsetY);
    }
  }

  // TODO - revise those two function names
  disableHover() {
    // this.element.eventMode = 'none'; // disables all interaction
  }
  enableHover() {
    // this.element.eventMode = 'dynamic';
  }
}
